/* eslint-disable camelcase */
/* eslint-disable max-len */
import fetch from 'isomorphic-fetch';

import {
	BB_LOCATION_API_URL,
	STOREFRONT_API_URL,
	STOREFRONT_API_ACCESS_TOKEN
} from './config';

/**
 * Shopify media types
 */
export const shopifyMediaTypes = {
	image: 'IMAGE',
	video: 'VIDEO',
	externalVideo: 'EXTERNAL_VIDEO',
};

/**
 * Fetch Shopify Storefront API data
 * @param {string} graphql query
 */
export const getShopifyData = (query, variables) => {
	return fetch(
		STOREFRONT_API_URL,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
			},
			body: JSON.stringify({ query, variables }),
		}
	)
		.then(response => {
			if (response.status !== 200) {
				throw new Error('cannot fetch data');
			}
			return response;
		})
		.then(response => {
			return response.json();
		})
		.then(json => {
			if (json.errors?.length) {
				// eslint-disable-next-line
				console.error(`Storefront API key may not be set in this Product's Sales Channel`, json.errors);
			}
			return json;
		})
		.catch(error => {
			// eslint-disable-next-line
			console.error(error);
			return null;
		});
};

export const getBBClassData = (query, variables) => {
	return fetch(
		process.env.GATSBY_BB_CLASS_GRAPHQL_URL,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ query, variables }),
		}
	)
		.then(response => {
			if (response.status !== 200 && response.status !== 400) {
				throw new Error('Fetching data failed');
			}
			return response.json();
		})
		.then(json => {
			if (json.errors?.length) {
				window.console.error(`BB Class API Error:`, json.errors);
			}
			return json; // {data, errors}
		})
		.catch(error => {
			return {data: null, errors: [{message: error.message}]};
		});
};

/**
 * Fetch Shopify Storefront API data
 * @param {string} graphql query
 * @param {object} object variables
 */
export const updateShopifyData = (query, variables) => {
	return fetch(
		STOREFRONT_API_URL,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
			},
			body: JSON.stringify({ query, variables }),
		}
	)
		.then(response => {
			if (response.status !== 200) {
				throw new Error('cannot update data');
			}
			return response;
		})
		.then(response => {
			return response.json();
		})
		.then(json => {
			if (json.errors?.length) {
				window.console.error(json.errors);
			}
			return json;
		})
		.catch(error => {
			window.console.error(error);
			return null;
		});
};

/**
 * Get an array of products given an array of product handles
 * @param {array} product handle
 */
export const getProductsByHandle = async(handlesArr = []) => {
	const promiseArray = [];

	handlesArr.forEach(handle => {
		const productQuery = `
			{
				product(handle: "${ handle }") {
					id
					title
					handle
					description
					priceRange {
						minVariantPrice {
							amount
							currencyCode
						}
						maxVariantPrice {
							amount
							currencyCode
						}
					}
					images(first: 1) {
						edges {
							node {
								altText
								originalSrc: url
							}
						}
					}
					media(first: 25) {
						edges {
							node {
								... on ExternalVideo {
							  		id
									alt
							  		host
							  		originUrl
							  		embedUrl
							  		previewImage {
										altText
										url
							  		}
								}
								... on Video {
							  		id
									alt
							 		sources {
										url
										format
										mimeType
							  		}
							  		previewImage {
										url
							  		}
								}
						  	}
						}
					}
					metafield(namespace: "custom_fields", key: "short_description") {
						key
						value
						namespace
					}
					collections(first: 250) {
						edges {
							node {
								title
								handle
							}
						}
					}
					options {
						id
						name
						values
					}
					variants (first: 250) {
						edges {
							node {
								id
								title
								availableForSale
							}
						}
					}
				}
			}
		`;

		promiseArray.push(getShopifyData(productQuery));
	});

	const response = await Promise.all(promiseArray);

	return response
		.filter(({ data }) => {
			return data?.product;
		})
		.map(({ data }) => {
			return {
				product: data.product,
			};
		});
};

/**
 * Get a Collection by its handle
 * @param {string} collection handle
 * @param {number} retrive the first x products
 */
export const getCollectionByHandle = async(handle = '', first = 3) => {
	const collectionQuery = `
		{
			collection(handle: "${ handle }") {
				title
				products(first: ${ first }) {
					edges {
						product: node {
							id
							title
							handle
							priceRange {
								minVariantPrice {
									amount
									currencyCode
								}
								maxVariantPrice {
									amount
									currencyCode
								}
							}
							images(first: 1) {
								edges {
									node {
										altText
										originalSrc: url(transform: {maxWidth: 430, maxHeight: 307})
									}
								}
							}
						}
					}
				}
			}
		}
	`;

	const response = await getShopifyData(collectionQuery);
	return response;
};

/**
 * Get an object of product variant, given a string of variant id
 * @param {string} variant id
 */
export const getProductVariantById = async(id = '') => {
	const productVariantQuery = `
		{
			variants: nodes(ids: "${ id }") {
    			... on ProductVariant {
      				storefrontId: id
      				title
      				sku
      				price {
        				amount
        				currency: currencyCode
        				currencyCode
      				}
					availableForSale
					requiresShipping
      				product {
        				storefrontId: id
        				shopifyId: id
        				title
       					productType
      				}
    			}
  			}
		}
	`;

	const response = await getShopifyData(productVariantQuery);
	const variant = response?.data?.variants?.[0];
	return variant;
};

/**
 * Get an array of variants, given a string of variant id
 * @param {array} variant id
 */
export const getProductVariantsByIds = async(ids = []) => {
	const productVariantQuery = `
		query getProductVariants($ids: [ID!]!) {
			variants: nodes(ids: $ids) {
    			... on ProductVariant {
      				storefrontId: id
      				title
      				sku
      				price {
        				amount
        				currencyCode
      				}
					availableForSale
					requiresShipping
      				product {
        				storefrontId: id
        				shopifyId: id
        				title
       					productType
      				}
    			}
  			}
		}
	`;

	const variables = {
		ids,
	};

	const response = await getShopifyData(productVariantQuery, variables);
	const variants = response?.data?.variants;
	return variants;
};

export const getBBClassByVariantId = async(id = '') => {
	const classQuery = `
		{
			registrationInfo(shopifyVariantId: "${ id }") {
				addons {
					physical {
						variantId: shopifyId
						isRequired
					}
					digital {
						sku
						price {
							amount
							currency
						}
						title
						taxcode
						isRequired
					}
					streaming {
						sku
						price {
							amount
							currency
						}
						title
						taxcode
						isRequired
					}
				}
				cancellationPolicyHtml: cancellation_policy_html
				cecs
				classscheduleId
				title
				courseType
				educator {
					name
					url
					address {
						addressLine1
						addressLine2
						city
						stateProvince
						zipCode
						country
						latitude
						longitude
					}
				}
				educatorContact: educator {
					contactName
					contactPhone
					contactEmail
				}
				endDate
				format
				handle
				hours
				instructors {
					name
					is_mi
					masterinstructorid
					instructorid
					title
				}
				internationalClass
				isSpotAvailable
				language
				latitude
				longitude
				notes
				price: pricing {
					amount
					currency
					early
					regular
					late
					early_at
					regular_at
					late_at
				}
				productId
				registerOffline
				registrationContact {
					registrantName: name
					registrantPhone: phone
					registrantEmail: email
					registrantUrl: url
				}
				sessions {
					start_date_time
					start_date_time_isdaylight
					end_date_time
					end_date_time_isdaylight
					time_zone_name
					time_zone_abbrv
				}
				sku
				startDate
				taxcode
				taxIncluded
				training
				variantId
				workshop
			}
		}		
	`;

	const response = await getBBClassData(classQuery);

	const data = response?.data?.registrationInfo || null;
	const error = response?.errors?.[0] || null;
	return { data, error };
};

export const getEducationFinderClasses = async({
	country = null,
	educator = null,
	latitude = null,
	longitude = null,
	radiusInMiles = null,
	training = null,
}) => {
	const inputFields = [];
	const efFilterFields = [];

	if (typeof country === 'string' && country.trim()) {
		inputFields.push(`country: ${ JSON.stringify(country) }`);
	}

	if (typeof educator === 'string' && educator.trim()) {
		inputFields.push(`educator: ${ JSON.stringify(educator) }`);
	}

	if (typeof latitude === 'number' && !Number.isNaN(latitude)) {
		inputFields.push(`latitude: ${ latitude }`);
	}

	if (typeof longitude === 'number' && !Number.isNaN(longitude)) {
		inputFields.push(`longitude: ${ longitude }`);
	}

	if (typeof radiusInMiles === 'number' && !Number.isNaN(radiusInMiles)) {
		inputFields.push(`radiusInMiles: ${ radiusInMiles }`);
	}

	if (typeof training === 'string' && training.trim()) {
		efFilterFields.push(`training: ${ JSON.stringify(training) }`);
	}

	const queryArgs = [`input: { ${ inputFields.join(' ') } }`];

	if (efFilterFields.length) {
		queryArgs.push(`effilters: { ${ efFilterFields.join(' ') } }`);
	}

	const educationFinderClassesQuery = `
		{
			courseDetailsInRange(
				${ queryArgs.join(', ') }
			) {
				courses {
					id: classscheduleId
					title
					handle
					latitude
					longitude
					format
					hours
					startDate
					endDate
					cecs
					instructors {
						name
						title
					}
					pricing {
						amount
						currency
					}
					educator {
						name
						address {
							addressLine1
							zipCode
							city
							stateProvince
							country
						}
					}
					training
					courseType
					language
				}
			}
		}
	`;

	const response = await getBBClassData(educationFinderClassesQuery);
	const data = response?.data?.courseDetailsInRange?.courses || [];
	const errors = response?.errors || [];
	return { data, errors };
};

/**
 * Determine if url is an external url
 * @param {string} url.
 */
export const isExternalUrl = url => {
	let isExternal = false;

	if (url && (url.includes('//') || url.match(/((^(mailto|tel|sms|mms|affirm):)|www.)/) || url.includes('#'))) {
		isExternal = true;
	}

	return isExternal;
};

/**
 * Used to set meta image in SEO component (from seo object or image object)
 * @param {object} image
 * @param {object} seo
 */
export const setMetaImage = (image, seo) => {
	let metaImage = null;
	if (seo?.metaImage) {
		metaImage = seo.metaImage.sourceUrl;
	} else if (image) {
		metaImage = image.sourceUrl;
	}

	return metaImage;
};

//	YouTube embed url (shared by getVideoEmbedData and getVideoEmbedSync).
const getYouTubeEmbedSrc = (videoId, params = '') => `https://www.youtube.com/embed/${ videoId }?autoplay=1&rel=0${ params }`;

/**
 * Synchronous (no network) sibling of getVideoEmbedData: embed url and type given a YT or
 * Vimeo url, built from the ids in the url itself so playback can start with no delay.
 * Covers public and unlisted Vimeo links (vimeo.com/<id>[/<hash>]); a video that only the
 * oEmbed endpoint can resolve (domain-restricted/private) needs getVideoEmbedData.
 * `enablejsapi` adds the YouTube JS API flag, needed to pause/resume the player via postMessage.
 * @param {string} url.
 * @param {{ enablejsapi?: boolean }} options.
 */
export const getVideoEmbedSync = (url = '', { enablejsapi = false } = {}) => {
	const youTube = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([\w-]{11})/);
	if (youTube) {
		return {
			type: 'youtube',
			src: getYouTubeEmbedSrc(youTube[1], enablejsapi ? '&enablejsapi=1' : ''),
		};
	}

	const vimeo = String(url).match(/vimeo\.com\/(?:video\/|channels\/[\w-]+\/)?(\d+)(?:\/([a-z0-9]+))?(?:\?.*h=([a-z0-9]+))?/i);
	if (vimeo) {
		const hash = vimeo[2] || vimeo[3];
		return {
			type: 'vimeo',
			src: `https://player.vimeo.com/video/${ vimeo[1] }?autoplay=1${ hash ? `&h=${ hash }` : '' }`,
		};
	}

	return false;
};

/**
 * Return embed url and type given a YT or Vimeo url
 * @param {string} url.
 */
export const getVideoEmbedData = async url => {
	if (!url) return false;

	const pattern = /(\/\/.+\/)(.+v=)?([a-zA-Z0-9-_]+)($|\?.+)/;
	const matches = url.match(pattern);

	if (!matches) return false;

	const videoId = matches[3]; // Video ID is 3rd capturing group.
	let src;
	let type;

	if (url.indexOf('youtu') !== -1) {
		type = `youtube`;
		src = getYouTubeEmbedSrc(videoId);
	} else if (url.indexOf('vimeo') !== -1) {
		try {
			const response = await fetch(`https://vimeo.com/api/oembed.json?url=${ encodeURIComponent(url) }`);
			if (!response.ok) {
				throw new Error('Network response was not ok');
			}
			const data = await response.json();
			type = `vimeo`;
			[, src] = data.html.match(/src="([^"]+)"/) || [];
		} catch (error) {
			return false;
		}
	} else {
		return false;
	}

	return {
		type,
		src,
	};
};

/**
 * Return parsed json or false
 * @param {string} JSON.
 */

export const parseJson = json => {
	let output = false;

	if (json) {
		try {
			output = JSON.parse(json);
			return output;
		} catch {
			// eslint-disable-next-line
			console.error('error parsing related products');
		}
	}

	return output;
};

/**
 * Read a fetch Response body as JSON without throwing.
 * A failed request (res.ok === false) can still carry a useful JSON error
 * body, but the body can also be empty or HTML (502/timeout) where res.json()
 * would throw. Reads the body once as text, then parses defensively.
 * @param {Response} response - a fetch Response
 * @returns {Promise<object|null>} parsed JSON, or null when empty/not JSON
 */
export const readJsonSafe = async response => {
	const text = await response.text().catch(() => '');
	if (!text) return null;
	try {
		return JSON.parse(text);
	} catch (err) {
		return null;
	}
};

/**
 * Return Shopify metafield given a key
 * @param {array} metafields
 * @param {string} key
 */

export const getMetafield = (metafields = [], key = '') => {
	const output = metafields.find(field => {
		return field.key === key;
	});

	return output;
};

/**
 * Return product page url
 * @param {object} product
 */

/**
 * Shopify productTypes that represent educational content/orders
 * (classes, events, course manuals, streaming video, and digital
 * access products). Used to tag line items with
 * `_is_education_order` at add-to-cart time.
 */
export const EDUCATION_PRODUCT_TYPES = [
	'access',
	'class',
	'class_hidden',
	'class_other',
	'course_manual',
	'event',
	'event_class_prepost',
	'streaming_video',
];

/**
 * Check if a Shopify productType represents educational content
 * @param {string} productType
 * @return {boolean}
 */
export const isEducationProductType = productType => EDUCATION_PRODUCT_TYPES.includes(productType?.toLowerCase());

export const getProductPageUrl = product => {
	const { productType, handle } = product;
	let url;
	switch (productType.toLowerCase()) {
	case 'event':
		url = `/${ productType }/${ handle }`;
		break;

	case 'class':
	case 'class_hidden':
		url = `/class/${ handle }`;
		break;

	case 'event_class':
	case 'event_class_prepost':
	case 'course_manual':
	case 'streaming_video':
	case 'contrology':
	case 'access':
		break;

	default:
		url = `/products/${ handle }`;
	}
	return url;
};

/**
 * Check & set if user is in a distribrutor country
 * @param {array} wpDistributors - list of distributors managed in Wordpress
 * @param {function} setDistributor - store-context funciton to set the distributor (bool) variable
 */

export const resolveDistributor = async(wpDistributors, setDistributor, setLocationInfo) => {
	let parsedDistrubutorData;
	const localStorageKey = 'bb_distributor';
	const existingDistributorData = localStorage.getItem(localStorageKey) || null;

	// Check if local storage variable is past the expiration date

	const isExpired = expAt => {
		return expAt < Date.now();
	};

	// Check if country is in list of distributors (Wordpress)

	const isDistributor = iso => {
		return wpDistributors?.some(dis => {
			return dis.countryIso.toUpperCase() === iso.toUpperCase().trim();
		});
	};

	// Parse JSON data

	if (existingDistributorData) {
		try {
			parsedDistrubutorData = JSON.parse(existingDistributorData);
		} catch {
			// eslint-disable-next-line
			console.error('Error parsing distributor data JSON.');
		}
	}

	// If local storage data exists and is not expired

	if (parsedDistrubutorData && !isExpired(parsedDistrubutorData?.expiresAt)) {
		setLocationInfo(() => parsedDistrubutorData);

		// If country is in the list of distributors, change distributor flag to true
		if (isDistributor(parsedDistrubutorData?.countryIso)) {
			setDistributor(true);
		} else {
			setDistributor(false);
		}
	} else {
		// Otherwise fetch new location data from api

		await fetch(BB_LOCATION_API_URL, {
			headers: {
				Accept: 'application/json',
			},
		})
			.then(async response => {
				switch (true) {
				//	HTTP 2xx
				case /^2\d{2}/.test(response.status):
					return response.json();
				//	HTTP xxx
				default:
					const message = await response.text(); // eslint-disable-line
					throw new Error(`We could not get geolocation data: ${ message }`);
				}
			})
			.then(responseData => {
				if (!responseData) return;

				const plus1Day = 1000 * 60 * 60 * 24;
				const { country, country_iso: countryIso } = responseData;
				const expiresAt = Date.now() + plus1Day;

				const data = {
					country: country || '',
					countryIso: countryIso || '',
					expiresAt,
				};

				// Set new local storage data

				localStorage.setItem(localStorageKey, JSON.stringify(data));

				setLocationInfo(data);

				// Update distributor flag

				if (isDistributor(countryIso)) {
					setDistributor(true);
				} else {
					setDistributor(false);
				}
			})
			.catch(error => {
				// eslint-disable-next-line
				console.error(error);
				setDistributor(false);
				setLocationInfo(() => ({
					countryIso: 'US',
				}));
			});
	}
};

/**
 * Return class attr
 * @param {array} classScheduleIds
 */

export const getClassScheduleData = async(classScheduleIds = []) => {
	if (!classScheduleIds.length) return [];

	const url = `https://${ process.env.GATSBY_BB_API_HOST }/api/Event/VerifyClassAvailability`;

	const data = {
		classscheduleids: classScheduleIds,
	};

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			throw new Error('Network response was not ok');
		}

		return await response.json();
	} catch (error) {
		window.console.error('Error fetching class schedule data:', error);
		return [];
	}
};

/**
 * Return class attr
 * @param {array} lineitemAttributes
 */

export const getClassAttributes = async lineitemAttributes => {
	const classScheduleIds = lineitemAttributes
		.reduce((acc, attr) => {
			if (/_class_schedule_id_\d+/.test(attr.key)) acc.push(attr.value);
			return acc;
		}, []);

	const response = await getClassScheduleData(classScheduleIds);

	const classAttributes = classScheduleIds
		.map((id, i) => {
			const { class_title } = response.find(({ classschedule_id }) => classschedule_id.toLowerCase() === id.toLowerCase()) || {};
			if (!class_title) return null;
			return {
				key: `Class-${ i + 1 }`,
				value: class_title,
			};
		})
		.filter(Boolean);

	return classAttributes;
};

/**
 * Get event classes availability
 * @param {array} lineitems
 * @returns {object}
 */

export const getEventClassesAvailability = async(lineitems = []) => {
	const eventLineItems = lineitems.filter(lineItem => {
		return lineItem.variant.product.productType === 'event'
			|| lineItem.variant.product.productType === 'event_class_prepost';
	});

	const eventLineItemsIDs = [];
	const eventClassesIds = [];
	let eventClassesData = [];
	let allEventClassesAvailable = false;

	if (eventLineItems.length) {
		eventLineItems
			.forEach(eventLineItem => {
				eventLineItemsIDs.push(eventLineItem.id);
				eventLineItem.attributes
					.forEach(attr => {
						if (/_class_schedule_id_\d+/.test(attr.key)) eventClassesIds.push(attr.value);
					});
			});
		eventClassesData = await getClassScheduleData(eventClassesIds);
		allEventClassesAvailable = eventClassesData.every(eventClass => eventClass.is_available);
	}

	return {
		eventLineItemsIDs,
		eventClassesData,
		allEventClassesAvailable,
	};
};

/**
 * Check if product is color swatches
 * @param {object} product
 * @returns {boolean}
 */

export const isColorSwatchesProduct = product => {
	return product?.handle === 'upholstery-color-swatches';
};
